'use strict';

export class WebsocketConnection {
	#websocket;
	#notifications;
	#notificationStack;

	constructor() {
		this.#checkWebsocketSettings();
		this.#initNotifications();
	}

	async #checkWebsocketSettings() {
		try {
			const response = await fetch(
				`${ window.location.origin }/wp-json/levcharity/v2/settings`,
				{
					method: 'GET',
					headers: {
						'X-WP-Nonce': wp_nonce_,
					},
				}
			);

			if ( ! response.ok ) {
				console.warn(
					'⚠️ Failed to fetch settings, WebSocket will not be initialized'
				);
				return;
			}

			const settings = await response.json();
			const websocketEnabled = settings?.advanced?.websockets?.enabled;

			if ( websocketEnabled && websocketEnabled !== '' ) {
				this.#initWebsocket();
			}
		} catch ( error ) {
			console.error( '❌ Error fetching settings:', error );
		}
	}

	#initWebsocket = () => {
		try {
			const protocol =
				window.location.protocol === 'https:' ? 'wss' : 'ws';
			const socketUrl = `${ protocol }://${ window.location.hostname }:16384`;
			this.#websocket = new WebSocket( socketUrl );
		} catch ( err ) {
			console.error( '❌ WebSocket creation failed:', err );
			return;
		}

		this.#websocket.onopen = () => {
			console.log( '✅ WebSocket connected!' );
		};

		this.#websocket.onmessage = async ( event ) => {
			try {
				if ( ! event.data ) {
					return;
				}

				let data;
				try {
					data = JSON.parse( event.data );
				} catch ( jsonErr ) {
					console.warn( '⚠️ Invalid JSON from WebSocket:', jsonErr );
					return;
				}

				if ( data.success ) {
					const order = data.data;

					if (
						! order.product_types ||
						! order.product_types.includes( 'campaign' )
					) {
						return;
					}

					let response;
					try {
						response = await this.fetchOrderDetails(
							order.order_id
						);
					} catch ( fetchErr ) {
						console.warn(
							'⚠️ Error fetching order details:',
							fetchErr
						);
						return;
					}

					if ( response !== null ) {
						this.#createNotification(
							response.modal.title,
							response.modal.content
						);
						this.updateTotalRaised( response );
					} else {
						console.warn( '⚠️ No valid content received.' );
					}

					try {
						const pageResp = await fetch( window.location.href, {
							method: 'GET',
						} );
						if ( ! pageResp.ok ) {
							throw new Error( 'Network error' );
						}

						const text = await pageResp.text();
						const parser = new DOMParser();
						const parsedData = parser.parseFromString(
							text,
							'text/html'
						);

						const donorContainer = document.querySelector(
							'.campaign_donors_container'
						);
						const newContent = parsedData.querySelector(
							'.campaign_donors_container'
						);

						if ( donorContainer && newContent ) {
							donorContainer.innerHTML = newContent.innerHTML;
						}
					} catch ( reloadErr ) {
						console.error(
							'❌ Error refreshing campaign donor section:',
							reloadErr
						);
					}
				}
			} catch ( e ) {
				console.error(
					'❌ Unexpected error in WebSocket message handler:',
					e
				);
			}
		};

		this.#websocket.onerror = ( error ) => {
			console.error( '⚠️ WebSocket error:', error );
		};

		this.#websocket.onclose = ( event ) => {
			console.warn( '❌ WebSocket cannot be established' );
		};
	};

	async fetchOrderDetails( order_id ) {
		const result = await fetch(
			`/wp-json/levcharity/v3/order/${ order_id }`,
			{
				method: 'GET',
				headers: {
					'X-WP-Nonce': wp_nonce_,
				},
			}
		)
			.then( ( resp ) => {
				if ( resp ) {
					return resp.json();
				}
				throw new Error( 'Network Error' );
			} )
			.then( ( data ) => {
				if ( data.success ) {
					return data;
				}
				throw new Error( 'Invalid response format' );
			} )
			.catch( ( error ) => {
				console.error( '⚠️ AJAX Error:', error );
				return { success: false, html: '' };
			} );
		return result;
	}

	#initNotifications() {
		if ( ! this.#notifications ) {
			this.#notifications = [];
		}

		if ( document.querySelector( '.lc-donation-notifications-list' ) ) {
			this.#notificationStack = document.querySelector(
				'.lc-donation-notifications-list'
			);
		} else {
			this.#notificationStack = document.createElement( 'div' );
			this.#notificationStack.classList.add(
				'lc-donation-notifications-list'
			);
			document.body.appendChild( this.#notificationStack );
		}
	}
	#createNotification( title, content, index = 0, force = false ) {
		if ( index == 0 ) {
			this.#notifications.push( {
				title,
				content,
			} );
		}
		if (
			Array.from( this.#notificationStack.children ).filter(
				( el ) => el.tagName === 'DIV'
			).length < 4 ||
			force
		) {
			const notification = document.createElement( 'div' );
			notification.classList.add( 'modal' );
			notification.dataset.notification = this.#notifications.length;
			notification.innerHTML = `<div class="head"><div class="title"><h3>${ title }</h3></div><div class="close"><a href="#" class="close-btn">&times;</a></div></div>`;
			notification.innerHTML += `<div class="content"></div>`;
			const _content = notification.querySelector( '.content' );
			if ( content.content ) {
				_content.innerHTML += `<p>${ content.content }</p>`;
			}
			if ( content.labels ) {
				Array.from( content.labels ).forEach( ( label ) => {
					_content.innerHTML += `<p>${ label.label }: <strong>${ label.value }</strong></p>`;
				} );
			}
			if ( content.actions ) {
				notification.innerHTML += `<div class="actions"></div>`;
				const _actions = notification.querySelector( '.actions' );
				Object.keys( content.actions ).forEach( ( action ) => {
					action = content.actions[ action ];
					_actions.innerHTML += `<div class="action"><a href="${
						action.url ?? '#'
					}"${ action.url ? ` target="_blank"` : '' }>${
						action.label
					}</a></div>`;
				} );
			}
			notification
				.querySelector( '.close-btn' )
				.addEventListener( 'click', ( e ) => {
					notification.classList.add( 'closed' );
					notification.style.animation = 'none';
					notification.offsetHeight;
					notification.style.animation = null;
					setTimeout( ( e ) => {
						notification.remove();
						if ( this.#notifications.length > 4 ) {
							const unshown =
								this.#notifications[
									this.#notifications.length - 1
								];
							this.#createNotification(
								unshown.title,
								unshown.content,
								this.#notifications.pop(),
								true
							);
						}
					}, 500 );
				} );

			this.#notificationStack.appendChild( notification );
		}
	}

	percentage( part, total ) {
		if ( total === 0 ) {
			return total;
		}
		return ( part / total ) * 100;
	}

	updateTotalRaised( response ) {
		const productType = response.product_type,
			totalAdded = response.total;

		if ( productType === 'campaign' ) {
			const raisedAmountElement = document.querySelector(
				'.campaign-goal-raised'
			);
			if ( raisedAmountElement ) {
				const raisedAmountPrefix =
					raisedAmountElement.getAttribute( 'data-prefix' );
				let raised = raisedAmountElement.getAttribute( 'data-to' );
				if ( raised.startsWith( raisedAmountPrefix ) ) {
					raised = raised
						.slice( raisedAmountPrefix.length )
						.replace( ',', '' );
				}
				raised = parseInt( raised ) + totalAdded;
				raisedAmountElement.setAttribute( 'data-to', raised );
				raisedAmountElement.textContent = `${ raisedAmountPrefix }${ raised.toLocaleString() }`;

				if (
					document.querySelector(
						'.amount.levcharity-count-up-animation'
					)
				) {
					const countUpAnimation = document.querySelector(
							'.amount.levcharity-count-up-animation'
						),
						progressTotal = document.querySelectorAll(
							'.levcharity-count-up'
						)[ 1 ];
					const percentageNew = this.percentage(
						raised,
						progressTotal.getAttribute( 'data-to' )
					);

					countUpAnimation.setAttribute( 'data-endvalue', raised );
					countUpAnimation.textContent = `${ raised.toLocaleString() }`;

					if (
						document.querySelector(
							'.levcharity_progressbar_percentage.levcharity-count-up'
						)
					) {
						const lcProgress = document.querySelector(
							'.levcharity_progressbar_percentage.levcharity-count-up'
						);
						lcProgress.setAttribute(
							'data-to',
							percentageNew + 100
						);
						lcProgress.innerText = `${ Math.round(
							percentageNew + 100
						) }${ lcProgress.getAttribute( 'data-suffix' ) }`;
					}
					if (
						document.querySelector(
							'.levcharity_progressbar_container'
						)
					) {
						const lcProgressBar =
							document.querySelector(
								'.levcharity_secondary_progressbar'
							) ??
							document.querySelector( '.levcharity_progressbar' );
						if ( lcProgressBar ) {
							let previousStyle =
									lcProgressBar.getAttribute( 'style' ),
								match = previousStyle.match(
									/--width:\s*(\d+(\.\d+)?)/
								),
								extractedWidth = 0;
							if ( match && match[ 1 ] ) {
								extractedWidth = parseFloat( match[ 1 ] );
							}
							if ( extractedWidth > 0 ) {
								const result =
									extractedWidth +
									Math.sqrt( percentageNew ) / percentageNew;
								lcProgressBar.setAttribute(
									'style',
									previousStyle.replace(
										extractedWidth,
										result
									)
								);
							}
						}
					}

					if ( document.querySelector( '.progressbar' ) ) {
						const progressBar =
								document.querySelector( '.progressbar' ),
							progressSecondary = document.querySelector(
								'.levcharity-count-up-animation.progressbar-text-content'
							);
						progressBar.setAttribute(
							'data-value',
							Math.round( percentageNew )
						);
						progressBar.setAttribute(
							'style',
							`--progress-value: ${ Math.round(
								percentageNew
							) }%;`
						);
						progressSecondary.setAttribute(
							'data-endvalue',
							Math.round( percentageNew )
						);
						progressSecondary.innerText =
							Math.round( percentageNew );
					}
				}
			}

			const stickyRaisedElement = document.querySelector(
				'.campaign-sticky-bar-info-item:nth-child(2) .title'
			);
			if ( stickyRaisedElement ) {
				stickyRaisedElement.textContent = response.friendly_total;
			}

			const donorCountElement = document.querySelector(
					'.campaign-sticky-bar-info-item:nth-child(1) .title'
				),
				donorCountContainerElement = document.querySelector(
					'.wp-block-custom-campaign-number-of-donors-levit-block'
				);
			if ( donorCountElement ) {
				const currentDonorCount =
					parseInt(
						donorCountElement.textContent.replace( /[^0-9]/g, '' )
					) || 0;
				const newDonorCount = currentDonorCount + 1;
				donorCountElement.textContent = newDonorCount;
				donorCountContainerElement.innerHTML = `<h3 class="levcharity_heading md-size bold text__navy">${ newDonorCount } donors</h3>`;
			}
		}
	}
}
